import { createElement, useMemo } from 'react';
import { useGT } from 'gt-react';
import type { WorkbookDefinition } from '@erp/shared-types';
import {
  referencePlainText,
  workbookReferences,
  type WorkbookItemKind,
  type WorkbookReference,
} from '@erp/workbooks';
import type {
  MentionKind,
  MentionSource,
} from '@/components/common/lexical-mentions/types';
import { WorkbookItemIcon } from './workbookItemIcon';
import { workbookItemIconKind } from './workbookItemIconKind';

/** How the Workbook page presents References: one resolver, translated labels. */
export interface WorkbookReferenceLabels {
  resolve(id: string): WorkbookReference;
  /** The Item's current name, or "Removed item". */
  name(id: string): string;
  /** One Markdown line as plain text, References replaced by names. */
  plainText(markdown: string): string;
  /** The single mention source every Instruction editor on the page shares. */
  mentionSources: MentionSource[];
}

const mentionKinds: Record<WorkbookItemKind, MentionKind> = {
  file: 'file',
  source: 'data',
  sheet: 'worksheet',
  output: 'file',
};

export function useWorkbookReferences(
  definition: WorkbookDefinition,
  openItem: (id: string) => void,
): WorkbookReferenceLabels {
  const gt = useGT();
  const { files, sources, sheets, outputs } = definition;
  return useMemo(() => {
    const references = workbookReferences({ files, sources, sheets, outputs });
    const removed = gt('Removed item', {
      context: 'workbook missing instruction reference',
    });
    const label = (reference: WorkbookReference) =>
      reference.kind === 'missing' ? removed : reference.name;
    const groups: { kind: WorkbookItemKind; label: string }[] = [
      {
        kind: 'file',
        label: gt('Files', { context: 'workbook instruction references' }),
      },
      {
        kind: 'source',
        label: gt('Data sources', {
          context: 'workbook instruction references',
        }),
      },
      {
        kind: 'sheet',
        label: gt('Worksheets', { context: 'workbook instruction references' }),
      },
      {
        kind: 'output',
        label: gt('Outputs', { context: 'workbook instruction references' }),
      },
    ];
    const mentionSource: MentionSource = {
      key: 'workbook',
      label: gt('Workbook items', { context: 'workbook mention picker' }),
      getGroups: (query) =>
        groups.map((group) => ({
          label: group.label,
          options: references.items
            .filter(
              (item) =>
                item.kind === group.kind &&
                item.name.toLowerCase().includes(query.toLowerCase()),
            )
            .map((item) => ({ id: item.id, displayName: item.name })),
        })),
      resolveDisplayName: (id) => {
        const reference = references.resolve(id);
        return reference.kind === 'missing' ? undefined : reference.name;
      },
      missingLabel: removed,
      resolveKind: (id) => {
        const reference = references.resolve(id);
        return reference.kind === 'missing'
          ? undefined
          : mentionKinds[reference.kind];
      },
      renderIcon: (id) => {
        const kind = workbookItemIconKind(
          { files, sources, sheets, outputs },
          id,
        );
        return kind
          ? createElement(WorkbookItemIcon, {
              kind,
              size: 10,
              'aria-hidden': true,
            })
          : undefined;
      },
      onActivate: openItem,
    };
    return {
      resolve: references.resolve,
      name: (id) => label(references.resolve(id)),
      plainText: (markdown) => referencePlainText(references, markdown, label),
      mentionSources: [mentionSource],
    };
  }, [files, sources, sheets, outputs, gt, openItem]);
}
